import type { UserRole } from 'types/auth.types';
import type { VehicleListItem } from 'types/api.generated';
import { ROUTES } from './routes';

export const resolveProviderRoute = (
  role: UserRole,
  vehicles: VehicleListItem[],
): string => {
  if (role !== 'provider') return ROUTES.CUSTOMER.DASHBOARD;

  if (vehicles.length === 0) {
    return ROUTES.ONBOARDING.SETUP;
  }

  return ROUTES.PROVIDER.DASHBOARD;
};

/** Vehicle still needs document submission (not waiting on admin review). */
export const vehicleNeedsVerification = (v: VehicleListItem): boolean =>
  ['unverified', 'rejected', 'expired'].includes(v.verificationStatus);

export const vehicleIsPendingReview = (v: VehicleListItem): boolean =>
  v.verificationStatus === 'pending';

export const vehicleCanGoOnline = (v: VehicleListItem): boolean =>
  v.verificationStatus === 'verified' &&
  !(v.latestVerification?.documents?.some((d) => d.isExpired) ?? false);

const VERIFICATION_LABELS: Record<VehicleListItem['verificationStatus'], string> = {
  unverified: 'Not verified',
  pending: 'Under review',
  verified: 'Verified',
  rejected: 'Rejected',
  expired: 'Expired',
};

export const vehicleVerificationLabel = (v: VehicleListItem): string =>
  VERIFICATION_LABELS[v.verificationStatus];

export const vehicleOnlineBlockReason = (v: VehicleListItem): string | null => {
  if (vehicleCanGoOnline(v)) return null;
  if (vehicleIsPendingReview(v)) {
    return 'This vehicle is awaiting verification review. You can go online once it is approved.';
  }
  if (vehicleNeedsVerification(v)) {
    return 'Submit vehicle documents before going online with this vehicle.';
  }
  if (v.verificationStatus === 'verified') {
    return 'One or more documents have expired. Update verification to go online.';
  }
  return 'This vehicle cannot go online yet.';
};
